import React from "react";
import { Hourglass, Repeat } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";
import { CalendarItem } from "../../features/calendar/models/CalendarItem";
import { OccurrenceStatus } from "../../features/sessions/models/OccurrenceStatus";
import { isAttendanceUnrecorded } from "../../features/calendar/utils/calendar-attention";
import { selectColleagues } from "../../features/instructors/instructors-slice";
import { instructorAccentClass } from "../../features/instructors/utils/instructorAccent";
import {
  calculateEndTimeDisplay,
  formatTimeDisplay,
} from "../../features/sessions/utils/DisplayMappers";
import { ROUTES } from "../../routes";

interface CalendarItemChipProps {
  item: CalendarItem;
  /** Compact chips (month cells) show only the start time; full chips (week columns) show the range. */
  compact?: boolean;
  /** Set when the calendar allows dragging this occurrence to another day. */
  draggable?: boolean;
  /** True while this chip is the one being dragged. */
  isDragging?: boolean;
  onDragStart?: (event: React.DragEvent) => void;
  onDragEnd?: () => void;
}

/**
 * One occurrence on the calendar grid. Cancelled occurrences stay visible but
 * struck-through; clicking opens the session detail. Active occurrences open the attendance screen.
 */
const CalendarItemChip: React.FC<CalendarItemChipProps> = ({
  item,
  compact = false,
  draggable = false,
  isDragging = false,
  onDragStart,
  onDragEnd,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cancelled = item.status === OccurrenceStatus.Cancelled;
  // Per-instructor accent only makes sense once the organization has colleagues.
  const colleagues = useAppSelector(selectColleagues);
  const multiInstructor = colleagues.length > 1;
  // A past session whose attendance was never marked — the gap that quietly
  // under-bills month-end, so it is called out right on the calendar.
  const unrecorded = isAttendanceUnrecorded(item, new Date());
  const notRecordedLabel = t("sessions.attendance.notRecorded");
  const isFull = item.capacity != null && item.inviteeCount >= item.capacity;

  // Tooltip: title (with the instructor in a multi-instructor organization),
  // then the cancellation reason and when it was cancelled.
  const titleWithInstructor =
    multiInstructor && item.instructorName
      ? `${item.title} — ${item.instructorName}`
      : item.title;
  const tooltip = cancelled
    ? [
        titleWithInstructor,
        item.reason,
        item.cancelledAt
          ? t("calendar.cancelledOn", {
              date: new Date(item.cancelledAt).toLocaleDateString(),
            })
          : undefined,
      ]
        .filter(Boolean)
        .join(" — ")
    : [titleWithInstructor, unrecorded ? notRecordedLabel : undefined]
        .filter(Boolean)
        .join(" — ");

  // Wall-clock time in the session's own zone — the same value the session
  // detail, forms, and attendance screens show (never the viewer's browser zone).
  const startTime = formatTimeDisplay(item.time);
  const timeRange = `${startTime} - ${calculateEndTimeDisplay(item.time, item.durationMinutes)}`;

  return (
    <button
      type="button"
      onClick={() =>
        navigate(
          cancelled
            ? ROUTES.SESSION_DETAILS.replace(":id", item.bookingId)
            : `${ROUTES.ATTENDANCE}/${item.bookingId}/${item.date}`
        )
      }
      title={tooltip}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`block w-full rounded-lg px-2 py-1 text-left text-xs transition-colors ${
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } ${isDragging ? "opacity-40" : ""} ${
        cancelled
          ? "bg-grey-95 text-grey-40 line-through opacity-70 hover:bg-grey-90 dark:bg-primary-20 dark:text-grey-70 dark:hover:bg-[#3E334D]"
          : "bg-primary-95 text-primary-30 hover:bg-primary-90 dark:bg-primary-20 dark:text-primary-95 dark:hover:bg-[#3E334D]"
      }`}
    >
      <span className="flex items-center gap-1">
        {multiInstructor && (
          <span
            aria-hidden="true"
            className={`inline-block h-2 w-2 shrink-0 rounded-full ${instructorAccentClass(item.instructorId)}`}
          />
        )}
        <span className="font-medium">{compact ? startTime : timeRange}</span>
        {item.isRecurring && (
          <Repeat
            size={12}
            className="shrink-0 opacity-70"
            aria-label={t("sessions.recurringSeries")}
          />
        )}
        {/* Compact cells have no room for the chip — the dot carries it, with the
            reason in the tooltip. */}
        {compact && unrecorded && (
          <span
            aria-label={notRecordedLabel}
            className="ms-auto inline-block h-2 w-2 shrink-0 rounded-full bg-secondary-40 dark:bg-secondary-60"
          />
        )}
      </span>
      <span className={`block truncate ${compact ? "" : "mt-0.5"}`}>{item.title}</span>
      {!compact && (item.inviteeCount > 0 || unrecorded || item.waitlistCount > 0) && (
        <span className="mt-0.5 flex flex-wrap items-center gap-1">
          {item.inviteeCount > 0 && (
            <span className={cancelled ? "text-grey-50 dark:text-grey-60" : "text-primary-40 dark:text-primary-90"}>
              {/* A capped session reads as a fill level ("5/6"); an uncapped one
                  keeps the plain member count. */}
              {item.capacity
                ? t("calendar.memberCapacity", {
                    count: item.inviteeCount,
                    capacity: item.capacity,
                  })
                : t("calendar.memberCount", { count: item.inviteeCount })}
            </span>
          )}
          {isFull && (
            <span className="rounded-full bg-grey-95 px-2 py-0.5 text-xs font-medium text-grey-30 dark:bg-primary-20 dark:text-grey-70">
              {t("calendar.sessionFull", "Full")}
            </span>
          )}
          {item.waitlistCount > 0 && (
            <span className="inline-flex items-center gap-1 text-grey-30 dark:text-grey-70">
              <Hourglass size={12} className="shrink-0" />
              {t("calendar.waitingCount", { count: item.waitlistCount })}
            </span>
          )}
          {unrecorded && (
            <span className="rounded-full bg-secondary-95 px-2 py-0.5 text-xs font-medium text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80">
              {notRecordedLabel}
            </span>
          )}
        </span>
      )}
    </button>
  );
};

export default CalendarItemChip;
